import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { BannerVariant } from '@/components/Common/Banner';

interface BannerConfig {
  message: string;
  variant?: BannerVariant;
  actionLabel?: string;
  onAction?: () => void;
  dismissible?: boolean;
  id?: string; // Unique ID to prevent duplicate banners
}

interface BannerContextType {
  banner: BannerConfig | null;
  setBanner: (banner: BannerConfig | null) => void;
  clearBanner: () => void;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const BannerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [banner, setBannerState] = useState<BannerConfig | null>(null);

  const setBanner = useCallback((next: BannerConfig | null) => {
    setBannerState((prev) => {
      if (prev === null && next === null) return prev;
      if (
        prev &&
        next &&
        prev.id === next.id &&
        prev.message === next.message &&
        prev.variant === next.variant &&
        prev.actionLabel === next.actionLabel &&
        prev.dismissible === next.dismissible
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const clearBanner = useCallback(() => {
    setBannerState((prev) => (prev === null ? prev : null));
  }, []);

  const value = useMemo(
    () => ({ banner, setBanner, clearBanner }),
    [banner, setBanner, clearBanner]
  );

  return (
    <BannerContext.Provider value={value}>
      {children}
    </BannerContext.Provider>
  );
};

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error('useBanner must be used within a BannerProvider');
  }
  return context;
};


